export type TimesheetRange = { from: string; to: string };

/** The integration summary rejects longer ranges (inclusive of both ends). */
export const MAX_TIMESHEET_RANGE_DAYS = 31;

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAYS_PER_WEEK = 7;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function pad(value: number) {
  return String(value).padStart(2, '0');
}

export function toIsoDate(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Parses YYYY-MM-DD as a local-time date; null for malformed or impossible dates. */
export function parseIsoDate(value: unknown): Date | null {
  if (typeof value !== 'string') return null;
  const match = ISO_DATE_PATTERN.exec(value);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return toIsoDate(date) === value ? date : null;
}

function addDays(date: Date, days: number) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

/** Monday–Sunday week containing `date`, in local time. */
export function getWeekRange(date: Date = new Date()): TimesheetRange {
  const daysSinceMonday = (date.getDay() + 6) % DAYS_PER_WEEK;
  const monday = addDays(date, -daysSinceMonday);
  return { from: toIsoDate(monday), to: toIsoDate(addDays(monday, DAYS_PER_WEEK - 1)) };
}

export type TimesheetRangePreset = { label: string; range: TimesheetRange };

function dayRange(date: Date): TimesheetRange {
  return { from: toIsoDate(date), to: toIsoDate(date) };
}

function monthRange(year: number, month: number): TimesheetRange {
  return { from: toIsoDate(new Date(year, month, 1)), to: toIsoDate(new Date(year, month + 1, 0)) };
}

/** Quick picks for the range menu; each fits within MAX_TIMESHEET_RANGE_DAYS. */
export function getRangePresets(now: Date = new Date()): TimesheetRangePreset[] {
  const thisWeek = getWeekRange(now);
  const lastWeek = getWeekRange(addDays(now, -DAYS_PER_WEEK));
  return [
    { label: 'Today', range: dayRange(now) },
    { label: 'Yesterday', range: dayRange(addDays(now, -1)) },
    { label: 'This week', range: thisWeek },
    { label: 'Last week', range: lastWeek },
    { label: 'Last 2 weeks', range: { from: lastWeek.from, to: thisWeek.to } },
    { label: 'This month', range: monthRange(now.getFullYear(), now.getMonth()) },
    { label: 'Last month', range: monthRange(now.getFullYear(), now.getMonth() - 1) },
  ];
}

/** Moves the whole range by whole weeks, keeping its length. */
export function shiftRangeByWeeks(range: TimesheetRange, weeks: number): TimesheetRange {
  const from = parseIsoDate(range.from);
  const to = parseIsoDate(range.to);
  if (!from || !to) return getWeekRange();
  const days = weeks * DAYS_PER_WEEK;
  return { from: toIsoDate(addDays(from, days)), to: toIsoDate(addDays(to, days)) };
}

export function countRangeDays(range: TimesheetRange): number | null {
  const from = parseIsoDate(range.from);
  const to = parseIsoDate(range.to);
  if (!from || !to) return null;
  // Rounding absorbs the 23h/25h days around daylight-saving changes.
  return Math.round((to.getTime() - from.getTime()) / MS_PER_DAY) + 1;
}

export function getRangeError(range: TimesheetRange): string | null {
  const days = countRangeDays(range);
  if (days === null) return 'Choose a valid start and end date';
  if (days < 1) return 'The start date must be on or before the end date';
  if (days > MAX_TIMESHEET_RANGE_DAYS) {
    return `Choose a range of ${MAX_TIMESHEET_RANGE_DAYS} days or fewer`;
  }
  return null;
}

/** Keeps `from`/`to` only when together they form a valid range; otherwise both are dropped. */
export function validateTimesheetSearch(search: Record<string, unknown>): Partial<TimesheetRange> {
  const { from, to } = search;
  if (typeof from !== 'string' || typeof to !== 'string') return {};
  return getRangeError({ from, to }) ? {} : { from, to };
}

export function resolveTimesheetRange(search: Partial<TimesheetRange>, now: Date = new Date()) {
  if (search.from && search.to) return { from: search.from, to: search.to };
  return getWeekRange(now);
}
